import { RecognitionCard } from '@amelio/ui-library';

import { getRecognitionLabelColor } from './recognitionLabelColor';
import { RecognitionMessage } from './RecognitionMessage';
import { formatSentAt } from './recognitionSentAt';

import type { ReceivedRecognition } from '@/types/recognition';

import { useAmelioTranslations } from '@/hooks';
import { getProfilePictureUrl } from '@/utils/user';

interface ReceivedRecognitionCardProps {
    recognition: ReceivedRecognition;
    /**
     * Opens the Recognition feed on the recognition the reader clicked. Left out when that feed is
     * not theirs -- a manager reading an employee's portal -- and the card is then not clickable at
     * all rather than clickable to the wrong place.
     */
    onOpenFeed?: () => void;
}

/**
 * One received recognition, as the library's card draws it: the praise card the sender chose in
 * head, then who sent it, when, and what they wrote. The whole card leads to the Engagement feed,
 * where the reader can answer it -- when that feed is theirs to open.
 */
export const ReceivedRecognitionCard = ({
    recognition: {
        sentAt,
        messageHtml,
        sender,
        praiseCard,
        companyValues,
        likeCount,
        commentCount,
    },
    onOpenFeed,
}: ReceivedRecognitionCardProps) => {
    const { t, i18n } = useAmelioTranslations();
    const senderName = sender?.fullName ?? t('common.anonymous');

    return (
        <RecognitionCard
            imageUrl={praiseCard.imageUrl}
            label={praiseCard.label}
            labelColor={getRecognitionLabelColor(praiseCard.labelColor)}
            sender={{
                name: senderName,
                avatarUrl: getProfilePictureUrl(sender?.profilePictureUrl) || undefined,
                role: sender?.jobTitle,
                isAnonymous: !sender,
            }}
            sentAt={formatSentAt(sentAt, i18n.language)}
            message={<RecognitionMessage html={messageHtml} />}
            companyValues={companyValues}
            likeCount={likeCount}
            commentCount={commentCount}
            countLabels={{
                likes: t('employeePortalRecognitions.likeCount', { count: likeCount }),
                comments: t('employeePortalRecognitions.commentCount', { count: commentCount }),
            }}
            onClick={onOpenFeed}
            accessibleName={
                onOpenFeed && t('employeePortalRecognitions.openFeed', { sender: senderName })
            }
            dataTestId={'employee-portal-recognition-card'}
        />
    );
};
